import React, { useState, useEffect } from 'react';
import Game from './components/Game';
import Leaderboard from './components/Leaderboard';
import './App.css';

const WORKER_URL = 'https://cryptwalkergame-worker.patrick-vida.workers.dev';

function App() {
  const [gameState, setGameState] = useState('start');
  const [playerStats, setPlayerStats] = useState(null);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newCharacterName, setNewCharacterName] = useState('');
  const [loginUsernameOrEmail, setLoginUsernameOrEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [selectedClass, setSelectedClass] = useState(null);
  const [userId, setUserId] = useState(null);
  const [characterId, setCharacterId] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUsername, setCurrentUsername] = useState('');
  const [error, setError] = useState('');
  const [showNewCharacterForm, setShowNewCharacterForm] = useState(false);
  const [newCharacterClass, setNewCharacterClass] = useState(null);
  const [newCharacterInputName, setNewCharacterInputName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGameReady, setIsGameReady] = useState(false);

  const sanitizeInput = (input) => {
    if (typeof input !== 'string') return input;
    return input.trim().replace(/[<>"'%;()&+]/g, '');
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const getToken = () => localStorage.getItem('token');

  const authFetch = async (url, options = {}) => {
    const token = getToken();
    if (!token) {
      throw new Error('No authentication token found. Please log in.');
    }
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...(options.headers || {}),
    };
    const response = await fetch(url, { ...options, headers });
    if (response.status === 401) {
      setUserId(null);
      setCharacterId(null);
      setIsLoggedIn(false);
      setCurrentUsername('');
      setGameState('start');
      localStorage.removeItem('userId');
      localStorage.removeItem('characterId');
      localStorage.removeItem('isLoggedIn');
      localStorage.removeItem('currentUsername');
      localStorage.removeItem('token');
      throw new Error('Unauthorized - Token expired or invalid');
    }
    return response;
  };

  useEffect(() => {
    const storedUserId = localStorage.getItem('userId');
    const storedCharacterId = localStorage.getItem('characterId');
    const storedIsLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
    const storedUsername = localStorage.getItem('currentUsername');
    const storedToken = localStorage.getItem('token');

    if (storedUserId) setUserId(parseInt(storedUserId));
    if (storedIsLoggedIn && storedToken) setIsLoggedIn(true);
    if (storedUsername) setCurrentUsername(storedUsername);
    if (storedIsLoggedIn && storedUserId && !storedCharacterId) {
      setGameState('character-select');
    }
  }, []);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        const response = await fetch(`${WORKER_URL}/api/leaderboard`);
        if (!response.ok) throw new Error('Failed to fetch leaderboard');
        const data = await response.json();
        setLeaderboard(Array.isArray(data) ? data : []);
      } catch (error) {
        setError('Unable to load leaderboard');
        setLeaderboard([]);
      }
    };
    fetchLeaderboard();
  }, []);

  useEffect(() => {
    const fetchCharacters = async () => {
      if (isLoggedIn && userId) {
        try {
          const response = await authFetch(`${WORKER_URL}/api/characters?player_id=${userId}`);
          if (!response.ok) throw new Error('Failed to fetch characters');
          const data = await response.json();
          setCharacters(Array.isArray(data) ? data : []);
        } catch (error) {
          setError('Unable to load characters');
          setCharacters([]);
        }
      }
    };
    fetchCharacters();
  }, [isLoggedIn, userId]);

  const register = async () => {
    setError('');
    const sanitizedUsername = sanitizeInput(newUsername);
    const sanitizedEmail = sanitizeInput(newEmail);
    const sanitizedCharacterName = sanitizeInput(newCharacterName);

    if (!sanitizedUsername || sanitizedUsername.length < 3) {
      setError('Username must be at least 3 characters long.');
      return;
    }
    if (!newPassword || newPassword.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }
    if (!sanitizedEmail || !validateEmail(sanitizedEmail)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!sanitizedCharacterName || sanitizedCharacterName.length < 1) {
      setError('Character name is required.');
      return;
    }

    try {
      const response = await fetch(`${WORKER_URL}/api/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: sanitizedUsername,
          password: newPassword,
          email: sanitizedEmail,
          class: selectedClass || 'Warrior',
          character_name: sanitizedCharacterName,
        }),
      });
      const data = await response.json();
      if (response.ok && data.player_id && data.character_id && data.token) {
        setUserId(data.player_id);
        setCharacterId(data.character_id);
        setCurrentUsername(sanitizedUsername);
        setIsLoggedIn(true);
        setGameState('playing');
        setIsGameReady(true);

        localStorage.setItem('userId', data.player_id);
        localStorage.setItem('characterId', data.character_id);
        localStorage.setItem('isLoggedIn', 'true');
        localStorage.setItem('currentUsername', sanitizedUsername);
        localStorage.setItem('token', data.token);
      } else {
        setError(data.error || 'Registration failed');
      }
    } catch (error) {
      setError('Registration failed. Please try again.');
    }
  };

  const login = async () => {
    setError('');
    const sanitizedUsernameOrEmail = sanitizeInput(loginUsernameOrEmail);
    if (!sanitizedUsernameOrEmail || !loginPassword) {
      setError('Username/email and password are required.');
      return;
    }

    try {
      const response = await fetch(`${WORKER_URL}/api/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ usernameOrEmail: sanitizedUsernameOrEmail, password: loginPassword }),
      });
      const data = await response.json();
      if (response.ok && data.player_id && data.token) {
        setUserId(data.player_id);
        setCharacterId(null);
        setCurrentUsername(sanitizedUsernameOrEmail);
        setIsLoggedIn(true);
        setGameState('character-select');

        localStorage.setItem('userId', data.player_id);
        localStorage.setItem('characterId', '');
        localStorage.setItem('isLoggedIn', 'true');
        localStorage.setItem('currentUsername', sanitizedUsernameOrEmail);
        localStorage.setItem('token', data.token);
      } else {
        setError(data.error || 'Login failed');
      }
    } catch (error) {
      setError('Login failed. Please try again.');
    }
  };

  const startNewCharacter = async (playerClass, characterName) => {
    setError('');
    if (!isLoggedIn || !userId) {
      setError('Please log in to create a character.');
      return;
    }
    if (!playerClass) {
      setError('Please select a class before creating a character.');
      return;
    }
    const sanitizedCharacterName = sanitizeInput(characterName);
    if (!sanitizedCharacterName || sanitizedCharacterName.length < 1) {
      setError('Character name is required.');
      return;
    }

    try {
      const response = await authFetch(`${WORKER_URL}/api/new-character`, {
        method: 'POST',
        body: JSON.stringify({ playerId: userId, class: playerClass, character_name: sanitizedCharacterName }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create character');
      }

      const data = await response.json();
      if (data.player_id && data.character_id) {
        setUserId(data.player_id);
        setCharacterId(data.character_id);
        setSelectedClass(playerClass);
        setGameState('playing');
        setIsGameReady(true);

        localStorage.setItem('characterId', data.character_id);
        const charactersResponse = await authFetch(`${WORKER_URL}/api/characters?player_id=${userId}`);
        if (charactersResponse.ok) {
          const charactersData = await charactersResponse.json();
          setCharacters(Array.isArray(charactersData) ? charactersData : []);
        }
      } else {
        setError('Failed to create character: Invalid response');
      }
    } catch (error) {
      setError('Error creating character. Please try again.');
    }
  };

  const continueCharacter = async (id) => {
    setIsLoading(true);
    setCharacterId(id);
    localStorage.setItem('characterId', id);
    try {
      const response = await authFetch(`${WORKER_URL}/api/characters?player_id=${userId}`);
      if (!response.ok) throw new Error('Failed to fetch characters');
      const data = await response.json();
      const character = data.find(char => char.id === id);
      if (character) {
        setSelectedClass(character.class);
      } else {
        setError('Character not found');
      }
    } catch (error) {
      setError('Failed to load character class');
    }
    setGameState('playing');
    setTimeout(() => {
      setIsGameReady(true);
      setIsLoading(false);
    }, 100);
  };

  const handleStartNewCharacter = (playerClass) => {
    setNewCharacterClass(playerClass);
    setShowNewCharacterForm(true);
  };

  const handleCreateNewCharacter = () => {
    if (!newCharacterInputName) {
      setError('Character name is required.');
      return;
    }
    startNewCharacter(newCharacterClass, newCharacterInputName);
    setShowNewCharacterForm(false);
    setNewCharacterInputName('');
    setNewCharacterClass(null);
  };

  const handleCancelNewCharacter = () => {
    setShowNewCharacterForm(false);
    setNewCharacterInputName('');
    setNewCharacterClass(null);
    setError('');
  };

  const updateLeaderboard = async (player, floorsCleared) => {
    if (!player?.playerId) {
      return;
    }
    try {
      const response = await authFetch(`${WORKER_URL}/api/leaderboard`, {
        method: 'POST',
        body: JSON.stringify({
          player_id: player.playerId,
          floors_cleared: floorsCleared,
          gold: player.gold || 0,
          kills: player.kills || 0,
          level: player.level || 1,
          character_name: player.character_name || 'Unknown',
          username: currentUsername,
        }),
      });
      if (!response.ok) {
        throw new Error('Failed to update leaderboard');
      }
      const leaderboardResponse = await fetch(`${WORKER_URL}/api/leaderboard`);
      const leaderboardData = await leaderboardResponse.json();
      setLeaderboard(Array.isArray(leaderboardData) ? leaderboardData : []);
    } catch (error) {
      setError('Failed to update leaderboard');
    }
  };

  const shareOnX = () => {
    const text = `I cleared ${playerStats?.floorsCleared || 0} floors in Crypt Crawler! Kills: ${playerStats?.kills || 0}, Gold: ${playerStats?.gold || 0}. Play now! https://cryptcrawler.pages.dev/`;
    window.open(`https://x.com/intent/tweet?text=${encodeURIComponent(text)}`);
  };

  return (
    <div className="app">
      {isLoading && <div className="loading-screen"><p>Loading game...</p></div>}
      {!isLoading && gameState === 'start' && (
        <div className="start-screen">
          <header className="start-header">
            <h1>CRYPT CRAWLER</h1>
          </header>
          <main className="start-content">
            <section className="leaderboard-section">
              <h2>Leaderboard</h2>
              {leaderboard.length > 0 ? (
                <div className="leaderboard-wrapper">
                  <table className="leaderboard-table">
                    <thead>
                      <tr>
                        <th>Rank</th>
                        <th>Player</th>
                        <th>Floors</th>
                        <th>Gold</th>
                        <th>Kills</th>
                        <th>Level</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leaderboard.map((entry, index) => (
                        <tr key={entry.id || index}>
                          <td>{index + 1}</td>
                          <td>{sanitizeInput(entry.character_name)} ({sanitizeInput(entry.username)})</td>
                          <td>{entry.floors_cleared}</td>
                          <td>{entry.gold || 0}</td>
                          <td>{entry.kills || 0}</td>
                          <td>{entry.level || 1}</td>
                          <td>{new Date(entry.timestamp || entry.last_played).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p>Loading leaderboard...</p>
              )}
              <button className="action-button" onClick={() => setGameState('leaderboard')}>
                Full Leaderboard
              </button>
            </section>
            <section className="auth-section">
              <h2>Login</h2>
              <form className="auth-form">
                <div className="form-group">
                  <input
                    type="text"
                    value={loginUsernameOrEmail}
                    onChange={(e) => setLoginUsernameOrEmail(e.target.value)}
                    placeholder="Username or Email"
                  />
                </div>
                <div className="form-group">
                  <input
                    type="password"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    placeholder="Password"
                  />
                </div>
                {error && <p className="error">{error}</p>}
                <div className="form-actions">
                  <button type="button" className="action-button" onClick={login}>
                    Login
                  </button>
                  <button
                    type="button"
                    className="action-button"
                    onClick={() => setGameState('register')}
                  >
                    Create New Account
                  </button>
                </div>
              </form>
            </section>
          </main>
        </div>
      )}
      {!isLoading && gameState === 'register' && (
        <div className="start-screen">
          <header className="start-header">
            <h1>CRYPT CRAWLER</h1>
          </header>
          <main className="start-content">
            <section className="auth-section">
              <h2>Create New Account</h2>
              <form className="auth-form">
                <div className="form-group">
                  <input
                    type="text"
                    value={newUsername}
                    onChange={(e) => setNewUsername(e.target.value)}
                    placeholder="Username"
                  />
                </div>
                <div className="form-group">
                  <input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="Email"
                  />
                </div>
                <div className="form-group">
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Password"
                  />
                </div>
                <div className="form-group">
                  <input
                    type="text"
                    value={newCharacterName}
                    onChange={(e) => setNewCharacterName(e.target.value)}
                    placeholder="Character Name"
                  />
                </div>
                <div className="class-options">
                  <button type="button" onClick={() => setSelectedClass('Warrior')}>
                    Warrior
                  </button>
                  <button type="button" onClick={() => setSelectedClass('Rogue')}>
                    Rogue
                  </button>
                  <button type="button" onClick={() => setSelectedClass('Mage')}>
                    Mage
                  </button>
                </div>
                {error && <p className="error">{error}</p>}
                <div className="form-actions">
                  <button type="button" className="action-button" onClick={register}>
                    Create Account
                  </button>
                  <button
                    type="button"
                    className="action-button"
                    onClick={() => setGameState('start')}
                  >
                    Back to Login
                  </button>
                </div>
              </form>
            </section>
          </main>
        </div>
      )}
      {!isLoading && gameState === 'character-select' && isLoggedIn && (
        <div className="start-screen">
          <header className="start-header">
            <h1>CRYPT CRAWLER</h1>
          </header>
          <main className="start-content">
            <section className="character-section">
              <h2>Your Characters</h2>
              {characters.length === 0 ? (
                <p>No characters found. Create a new one!</p>
              ) : (
                <ul className="character-list">
                  {characters.map((char) => (
                    <li key={char.id} className="character-item">
                      <span>
                        {char.character_name} (Class: {char.class}) - {char.is_alive ? 'Alive' : 'Dead'} (Last Played: {new Date(char.last_played).toLocaleDateString()})
                      </span>
                      <button className="action-button" onClick={() => continueCharacter(char.id)}>
                        Continue
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <h2>Create New Character</h2>
              {showNewCharacterForm ? (
                <div className="new-character-form">
                  <div className="form-group">
                    <input
                      type="text"
                      value={newCharacterInputName}
                      onChange={(e) => setNewCharacterInputName(e.target.value)}
                      placeholder="Character Name"
                    />
                  </div>
                  <div className="form-actions">
                    <button className="action-button" onClick={handleCreateNewCharacter}>
                      Create
                    </button>
                    <button className="action-button" onClick={handleCancelNewCharacter}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="class-options">
                  <button className="action-button" onClick={() => handleStartNewCharacter('Warrior')}>
                    Start as Warrior
                  </button>
                  <button className="action-button" onClick={() => handleStartNewCharacter('Rogue')}>
                    Start as Rogue
                  </button>
                  <button className="action-button" onClick={() => handleStartNewCharacter('Mage')}>
                    Start as Mage
                  </button>
                </div>
              )}
              <button
                className="action-button"
                onClick={() => {
                  setUserId(null);
                  setCharacterId(null);
                  setIsLoggedIn(false);
                  setCurrentUsername('');
                  setGameState('start');
                  localStorage.removeItem('userId');
                  localStorage.removeItem('characterId');
                  localStorage.removeItem('isLoggedIn');
                  localStorage.removeItem('currentUsername');
                  localStorage.removeItem('token');
                }}
              >
                Logout
              </button>
              {error && <p className="error">{error}</p>}
            </section>
          </main>
        </div>
      )}
      {!isLoading && gameState === 'playing' && characterId && isGameReady && (
        <Game
          setGameState={setGameState}
          updateLeaderboard={updateLeaderboard}
          selectedClass={selectedClass}
          characterId={characterId}
          shareOnX={shareOnX}
          setPlayerStats={setPlayerStats}
        />
      )}
      {!isLoading && gameState === 'leaderboard' && (
        <Leaderboard leaderboard={leaderboard} setGameState={setGameState} />
      )}
    </div>
  );
}

export default App;